import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Still Have Questions?' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * FAQ page intro: a centered stripe that opens the page below the hero/wave
 * band. Carries the headline, an FAQ blurb (bound to property_faqs_blurb so a
 * property-level override drops straight in), and a secondary CTA. Mirrors the
 * centered intro pattern used by FloorPlansIntro so the FAQ page reads as part
 * of the same site.
 */
export function FAQIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cta = useSlotGroup('faqintro.cta', ctaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-10 md:pb-16"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="faqintro.heading"
          label="Heading"
          defaultValue="<p>Frequently Asked Questions</p>"
          as="h2"
          className="font-heading text-balance text-[clamp(1.75rem,6vw,2.5rem)] font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-6"
        />

        <div className="max-w-3xl mx-auto">
          <RichTextContent
            path="faqintro.body"
            label="Body Text"
            defaultField="property_faqs_blurb"
            defaultValue={`<p>Have a question about living at <span data-type="field-token" data-field="property_name"></span>? Browse answers to the questions we hear most often, from leasing and pet policies to amenities and maintenance. Can't find what you're looking for? Our team is happy to help — just reach out.</p>`}
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div className="mt-6 flex justify-center">
          <EditableSlotGroup groupPath="faqintro.cta" definition={ctaDef}>
            <a
              href={cta.url || '/contact-us'}
              target={cta._target || '_self'}
              className="fqi-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {cta.label || 'Still Have Questions?'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .fqi-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .fqi-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQIntro',
  displayName: 'FAQ Intro',
  tokens: {
    'faqintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Frequently Asked Questions</p>',
    },
    'faqintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_faqs_blurb',
      defaultValue:
        '<p>Have a question about living at <span data-type="field-token" data-field="property_name"></span>? Browse answers to the questions we hear most often, from leasing and pet policies to amenities and maintenance. Can\'t find what you\'re looking for? Our team is happy to help — just reach out.</p>',
    },
  },
  slotGroups: {
    'faqintro.cta': ctaDef,
  },
};

(FAQIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQIntro;
